import { Box, Typography } from '@mui/material';
import { useInView } from 'react-intersection-observer';
import { useLocation } from 'react-router-dom';

import { Routes } from '@/constants';
import { Link } from '@/containers';
import { CheckmarkIcon } from '@/icons';
import { pinnedLpStar } from '@/images';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import { LearningPathWithDuration, Referrer } from '@/types';
import { createRouteWithParams } from '@/utils';

import { ProgressBar } from '../ProgressBar';
import {
  LearningPathCardDimensions,
  LearningPathCardVariants,
} from './constants';
import {
  CreditsDurationInfo,
  EpisodeCountWithCompletions,
} from './CreditsDurationInfo';

const { width, height, coverImageWidth, coverImageHeight } =
  LearningPathCardDimensions[LearningPathCardVariants.VERTICAL];

export const LearningPathVerticalCard = ({
  learningPath,
  referrer,
  Footer,
  PinButton,
  stretch,
}: {
  learningPath: LearningPathWithDuration;
  referrer: Referrer;
  Footer?: React.FC<{ learningPath: LearningPathWithDuration }>;
  PinButton?: React.FC<{ learningPath: LearningPathWithDuration }>;
  stretch?: boolean;
}) => {
  const { pathname, search } = useLocation();
  const location = {
    ...referrer,
    component: EVENT_CONSTANTS.COMPONENT.LEARNING_PATH_CARD,
    variants: {
      ...referrer.variants,
      lpCardVariant: LearningPathCardVariants.VERTICAL,
    },
  };

  const { ref } = useInView({
    threshold: 0,
    triggerOnce: true,
    onChange: inView => {
      if (inView) {
        AnalyticsService.learningPathImpression({
          learningPathId: learningPath.groupId,
          location,
          referrer,
        });
      }
    },
  });

  const episodeCount = learningPath.episodes.length || 0;
  const progressFraction = learningPath.duration
    ? Math.min(learningPath.durationPlayed / learningPath.duration, 1)
    : 0;

  return (
    <Link
      variant="plain"
      to={createRouteWithParams(Routes.LEARNING_PATH, {
        learningPathId: learningPath.groupId,
      })}
      onClick={() => {
        AnalyticsService.learningPathCardClicked({
          learningPathId: learningPath.groupId,
          location,
          referrer,
          episodesCompleted: learningPath.completions,
          isCompleted: learningPath.isCompleted,
          topic: learningPath.topic?.name ?? '',
          isRecommended: learningPath.isPinnedByOrg,
        });
      }}
      state={{
        referrer,
        from: { pathname, search },
      }}>
      <Box
        ref={ref}
        sx={[
          {
            position: 'relative',
            display: 'flex',
            backgroundColor: 'white',
            border: 1,
            borderColor: 'secondary.medium',
            borderRadius: 4,
            py: 1.5,
            px: 2,
            '&:hover': {
              borderColor: 'secondary.active',
              backgroundColor: 'secondary.light',
            },
            transition: 'border .1s ease, background-color .1s ease',
            '&:hover .cover-image': { transform: 'scale(1.1)' },
          },
          {
            height: '100%',
            width: stretch ? '100%' : width,
            maxWidth: stretch ? '100%' : width,
            maxHeight: stretch ? 'none' : height,
            aspectRatio: `${width} / ${height}`,
            flexDirection: 'column',
          },
        ]}>
        <Box sx={{ flex: 2 }}>
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            {PinButton ? (
              <PinButton learningPath={learningPath} />
            ) : learningPath.isPinnedByOrg ? (
              <Box
                component="img"
                src={pinnedLpStar}
                alt="pinned lp star"
                sx={{
                  width: 24,
                  height: 24,
                }}
              />
            ) : null}
          </Box>
          <Box
            sx={[
              {
                position: 'relative',
                mb: 2.5,
                overflow: 'hidden',
                borderRadius: '50%',
                mx: 'auto',
              },
              stretch
                ? {
                    width: '80%',
                    aspectRatio: `${coverImageWidth} / ${coverImageHeight}`,
                  }
                : {
                    width: coverImageWidth,
                    height: coverImageHeight,
                  },
            ]}>
            <Box
              sx={[
                {
                  width: '100%',
                  height: '100%',
                  transition: 'transform .25s ease-in-out',
                },
                learningPath.isCompleted && { filter: 'grayscale(1)' },
              ]}
              className="cover-image"
              component="img"
              src={learningPath.coverImage}
              alt="learning path cover"
            />
            {learningPath.isCompleted && (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.75)',
                }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'accent.main',
                  }}>
                  <CheckmarkIcon sx={{ width: 32, height: 32 }} />
                </Box>
              </Box>
            )}
          </Box>
          <Typography
            variant="h4"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
            {learningPath.title}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption1Bold">
            {learningPath.topic?.name}
          </Typography>
          <Box sx={{ width: '100%', mt: 3 }}>
            <ProgressBar
              progressFraction={progressFraction}
              skinny
              progressColor={
                learningPath.isCompleted ? 'accent.main' : 'secondary.main'
              }
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              flexWrap: 'wrap',
              mt: 1,
            }}>
            <CreditsDurationInfo
              credits={learningPath.credits}
              duration={learningPath.duration}
              durationPlayed={learningPath.durationPlayed}
            />
            <EpisodeCountWithCompletions
              episodeCount={episodeCount}
              completions={learningPath.completions}
            />
          </Box>
        </Box>
      </Box>
      {Footer && (
        <Box
          sx={{
            width: '100%',
            mt: 1.5,
          }}>
          <Footer learningPath={learningPath} />
        </Box>
      )}
    </Link>
  );
};
